import { supabase } from './supabase';

// Type definitions
export interface BranchRecommendation extends CollegeBranch {
  college: CollegeWithBranches;
  recommendation_type: 'Dream' | 'Target' | 'Safety';
  admission_probability: number;
  best_round?: CounselingRound;
  all_rounds: CounselingRound[];
}

export interface CollegeWithBranches {
  id: string;
  name: string;
  location: string;
  state: string;
  exam_type: string;
  college_type: string;
  category: string;
  cutoff_general?: number;
  cutoff_obc?: number;
  cutoff_sc?: number;
  cutoff_st?: number;
  cutoff_ews?: number;
  fees_annual: string;
  seats_total?: number;
  nirf_ranking?: number;
  placement_percentage?: number;
  average_package?: string;
  established?: string;
  website?: string;
  image_url: string;
  created_at?: string;
  updated_at?: string;
  slug: string;
  branches?: CollegeBranch[];
}

export interface CollegeBranch {
  id: string;
  college_id: string;
  branch_name: string;
  branch_code?: string;
  cutoff_general?: number;
  cutoff_obc?: number;
  cutoff_sc?: number;
  cutoff_st?: number;
  cutoff_ews?: number;
  seats_available?: number;
  duration?: string;
  degree_type?: string;
  created_at?: string;
  updated_at?: string;
  counseling_rounds?: CounselingRound[];
}

export interface CounselingRound {
  id: string;
  branch_id: string;
  round_number: number;
  round_name: string;
  year: string;
  cutoff_general?: number;
  cutoff_obc?: number;
  cutoff_sc?: number;
  cutoff_st?: number;
  cutoff_ews?: number;
  seats_filled?: number;
  created_at?: string;
  updated_at?: string;
}

// Helper function to check if a cutoff value is valid
const isValidCutoff = (value: any): value is number => {
  if (typeof value === 'number' && value > 0) return true;
  if (typeof value === 'string') {
    const normalized = value.toLowerCase().trim();
    return normalized !== 'n/a' && normalized !== 'na' && normalized !== '' && !isNaN(Number(value)) && Number(value) > 0;
  }
  return false;
};

// Helper function to get numeric cutoff value
const getNumericCutoff = (value: any): number | null => {
  if (typeof value === 'number' && value > 0) return value;
  if (typeof value === 'string') {
    const normalized = value.toLowerCase().trim();
    if (normalized === 'n/a' || normalized === 'na' || normalized === '') return null;
    const num = Number(value);
    return !isNaN(num) && num > 0 ? num : null;
  }
  return null;
};

export const personalizedCollegeService = {
  generateRecommendations: async (
    examType: string,
    rank: number,
    category: 'General' | 'OBC' | 'SC' | 'ST' | 'EWS',
    statePreferences?: string | string[],
    branchPreferences?: string[]
  ): Promise<BranchRecommendation[]> => {
    try {
      const examTypeMap: { [key: string]: string } = {
        'JEE Main': 'JEE_MAIN',
        'JEE Advanced': 'JEE_ADVANCED',
        'NEET': 'NEET'
      };

      const mappedExamType = examTypeMap[examType] || examType;
      const cutoffField = `cutoff_${category.toLowerCase()}` as keyof CounselingRound;

      let query = supabase
        .from('personalized_colleges')
        .select(`
          *,
          branches:college_branches(
            *,
            counseling_rounds:counseling_rounds(*)
          )
        `)
        .eq('exam_type', mappedExamType);

      if (statePreferences && statePreferences.length > 0) {
        const states = Array.isArray(statePreferences)
          ? statePreferences.filter(s => s && s !== 'All States' && s !== 'Any State')
          : [statePreferences];
        if (states.length > 0) query = query.in('state', states);
      }

      const { data: colleges, error } = await query;
      if (error || !colleges) throw error || new Error('No colleges found');

      const recommendations: BranchRecommendation[] = [];

      for (const college of colleges) {
        if (!college.branches) continue;

        for (const branch of college.branches) {
          const counselingRounds = branch.counseling_rounds || [];

          // Find the best (lowest) valid cutoff from all sources
          let bestCutoff: number | null = null;
          let bestRound: CounselingRound | undefined;
          const validRounds: CounselingRound[] = [];

          // Check counseling rounds for valid cutoffs
          for (const round of counselingRounds) {
            const roundCutoff = getNumericCutoff(round[cutoffField]);
            if (roundCutoff !== null) {
              validRounds.push(round);
              if (bestCutoff === null || roundCutoff < bestCutoff) {
                bestCutoff = roundCutoff;
                bestRound = round;
              }
            }
          }

          // Check branch cutoff if no valid round cutoff found or if branch cutoff is better
          const branchCutoff = getNumericCutoff(branch[cutoffField as keyof CollegeBranch]);
          if (branchCutoff !== null) {
            if (bestCutoff === null || branchCutoff < bestCutoff) {
              bestCutoff = branchCutoff;
              bestRound = undefined; // Use branch cutoff, not round cutoff
            }
          }

          // Skip if no valid cutoff found or student rank is higher than cutoff (not eligible)
          if (bestCutoff === null || rank > bestCutoff) continue;

          // Calculate probability based on margin
          const margin = bestCutoff - rank;
          const marginPercentage = (margin / bestCutoff) * 100;
          
          let admissionProbability: number;
          let recommendationType: 'Dream' | 'Target' | 'Safety';

          if (marginPercentage >= 50) {
            admissionProbability = Math.min(95, 85 + (marginPercentage - 50) / 5);
            recommendationType = 'Safety';
          } else if (marginPercentage >= 30) {
            admissionProbability = 75 + (marginPercentage - 30) / 2;
            recommendationType = 'Safety';
          } else if (marginPercentage >= 15) {
            admissionProbability = 65 + (marginPercentage - 15) / 1.5;
            recommendationType = 'Target';
          } else if (marginPercentage >= 5) {
            admissionProbability = 50 + (marginPercentage - 5);
            recommendationType = 'Target';
          } else {
            admissionProbability = 30 + marginPercentage * 4;
            recommendationType = 'Dream';
          }

          recommendations.push({
            ...branch,
            college,
            recommendation_type: recommendationType,
            admission_probability: Math.round(admissionProbability),
            best_round: bestRound,
            all_rounds: validRounds
          });
        }
      }

      // Sort by cutoff rank (ascending) - closest cutoffs first
      recommendations.sort((a, b) => {
        const aCutoff = a.best_round?.[cutoffField] || (a[cutoffField as keyof CollegeBranch] as number) || 999999;
        const bCutoff = b.best_round?.[cutoffField] || (b[cutoffField as keyof CollegeBranch] as number) || 999999;
        
        if (aCutoff !== bCutoff) return aCutoff - bCutoff;
        
        // Secondary sort by NIRF ranking (lower is better)
        const aNirf = a.college.nirf_ranking || 999999;
        const bNirf = b.college.nirf_ranking || 999999;
        if (aNirf !== bNirf) return aNirf - bNirf;
        
        // Tertiary sort by college name
        return a.college.name.localeCompare(b.college.name);
      });

      return recommendations.slice(0, 50);
    } catch (err) {
      console.error('Error generating recommendations:', err);
      return [];
    }
  },

  // College CRUD operations
  getAll: async (filters?: { search?: string; state?: string }) => {
    try {
      let query = supabase
        .from('personalized_colleges')
        .select(`
          *,
          branches:college_branches(
            *,
            counseling_rounds:counseling_rounds(*)
          )
        `)
        .order('name');
      
      // Apply search filter
      if (filters?.search) {
        query = query.or(`name.ilike.%${filters.search}%,location.ilike.%${filters.search}%`);
      }
      
      // Apply state filter
      if (filters?.state && filters.state !== 'all') {
        query = query.eq('state', filters.state);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error fetching colleges:', err);
      throw err;
    }
  },

  getById: async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('personalized_colleges')
        .select(`
          *,
          branches:college_branches(
            *,
            counseling_rounds:counseling_rounds(*)
          )
        `)
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error fetching college:', err);
      throw err;
    }
  },

  create: async (college: Omit<CollegeWithBranches, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { slug, branches, ...payload } = college as any;
      const { data, error } = await supabase
        .from('personalized_colleges')
        .insert(payload)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error creating college:', err);
      throw err;
    }
  },

  update: async (id: string, college: Partial<CollegeWithBranches>) => {
    try {
      // Strip non-column fields before sending to DB
      const { slug, branches, ...payload } = college as any;
      const { data, error } = await supabase
        .from('personalized_colleges')
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error updating college:', err);
      throw err;
    }
  },

  delete: async (id: string) => {
    try {
      const { error } = await supabase
        .from('personalized_colleges')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    } catch (err) {
      console.error('Error deleting college:', err);
      throw err;
    }
  },

  // Branch CRUD operations
  createBranch: async (branch: Omit<CollegeBranch, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('college_branches')
        .insert(branch)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error creating branch:', err);
      throw err;
    }
  },

  updateBranch: async (id: string, branch: Partial<CollegeBranch>) => {
    try {
      const { counseling_rounds, ...payload } = branch as any;
      const { data, error } = await supabase
        .from('college_branches')
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error updating branch:', err);
      throw err;
    }
  },

  deleteBranch: async (id: string) => {
    try {
      const { error } = await supabase
        .from('college_branches')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    } catch (err) {
      console.error('Error deleting branch:', err);
      throw err;
    }
  },

  // Counseling Round CRUD operations
  createCounselingRound: async (round: Omit<CounselingRound, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('counseling_rounds')
        .insert(round)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error creating counseling round:', err);
      throw err;
    }
  },

  updateCounselingRound: async (id: string, round: Partial<CounselingRound>) => {
    try {
      const { data, error } = await supabase
        .from('counseling_rounds')
        .update(round)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error updating counseling round:', err);
      throw err;
    }
  },

  deleteCounselingRound: async (id: string) => {
    try {
      const { error } = await supabase
        .from('counseling_rounds')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    } catch (err) {
      console.error('Error deleting counseling round:', err);
      throw err;
    }
  }
};